import { useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { KeyRound } from 'lucide-react';
import { useAuth } from '@/lib/auth';
import { useCanUsePasskeys } from '@/lib/passkeys';
import { Button, Card, ErrorText, Field, Input } from '@/components/ui';

/**
 * Passwordless sign-in. One email box covers sign-up and sign-in: a 6-digit
 * code arrives by email. People who have added a passkey can use it instead.
 */
export function SignInPage() {
  const { mode, startEmailSignIn, confirmCode, resendCode, signInWithPasskey } = useAuth();
  // Only offer a passkey where this device can use one.
  const canUsePasskeys = useCanUsePasskeys();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const next = params.get('next') ?? '/home';

  const [step, setStep] = useState<'email' | 'code'>('email');
  const [email, setEmail] = useState('');
  const [code, setCode] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<unknown>(null);
  const [notice, setNotice] = useState<string | null>(null);

  const run = async (fn: () => Promise<void>) => {
    setBusy(true);
    setError(null);
    setNotice(null);
    try {
      await fn();
    } catch (err) {
      setError(err);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="mx-auto max-w-sm">
      <Card title={step === 'email' ? 'Sign in or create an account' : 'Check your email'}>
        {step === 'email' ? (
          <form
            className="space-y-4"
            onSubmit={(e) => {
              e.preventDefault();
              void run(async () => {
                const result = await startEmailSignIn(email);
                if (result === 'signed_in') navigate(next);
                else setStep('code');
              });
            }}
          >
            <Field
              label="Email"
              htmlFor="email"
              hint={
                mode === 'dev'
                  ? 'Local development: no code is sent.'
                  : 'Use your university or work email to join your organization automatically.'
              }
            >
              <Input
                id="email"
                type="email"
                required
                autoComplete="username webauthn"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="you@university.edu"
              />
            </Field>
            <Button type="submit" className="w-full" disabled={busy}>
              Continue with email
            </Button>
            {canUsePasskeys && (
              <Button
                type="button"
                variant="secondary"
                className="w-full"
                disabled={busy || !email}
                onClick={() =>
                  void run(async () => {
                    await signInWithPasskey(email);
                    navigate(next);
                  })
                }
              >
                <KeyRound className="size-4" aria-hidden />
                Sign in with a passkey
              </Button>
            )}
            <ErrorText error={error} />
          </form>
        ) : (
          <form
            className="space-y-4"
            onSubmit={(e) => {
              e.preventDefault();
              void run(async () => {
                await confirmCode(code);
                navigate(next);
              });
            }}
          >
            <p className="text-sm text-muted-foreground">
              We sent a 6-digit code to <span className="font-medium text-foreground">{email}</span>
              .
            </p>
            <Field label="Code" htmlFor="code">
              <Input
                id="code"
                inputMode="numeric"
                autoComplete="one-time-code"
                pattern="[0-9]{6,8}"
                required
                value={code}
                onChange={(e) => setCode(e.target.value)}
              />
            </Field>
            <Button type="submit" className="w-full" disabled={busy}>
              Verify
            </Button>
            <div className="flex justify-between text-sm">
              <button
                type="button"
                className="text-muted-foreground hover:text-foreground"
                onClick={() => {
                  setStep('email');
                  setCode('');
                  setError(null);
                }}
              >
                Use a different email
              </button>
              <button
                type="button"
                className="text-primary"
                disabled={busy}
                onClick={() =>
                  void run(async () => {
                    await resendCode();
                    setNotice('A new code is on its way.');
                  })
                }
              >
                Resend code
              </button>
            </div>
            {notice && <p className="text-sm text-muted-foreground">{notice}</p>}
            <ErrorText error={error} />
          </form>
        )}
      </Card>
    </div>
  );
}
